// ============================================================
//  Plan-Exporte: PDF (Umsetzungsplan) und CSV (alle Personen je Position).
//  Beide lesen ausschließlich aus planResult — keine eigene Rechnung, keine erfundenen Zahlen.
// ============================================================
import type { Dataset } from "../data/generator";
import { landscapeById } from "../data/landscapes";
import type { PlanItemResult, PlanPersonRow, PlanResult } from "../domain/plan";
import { CLASS_LABEL, formatEuro, formatFue, formatFue1, formatNum } from "../domain/rules";
import type { LicenseClass, TraceState } from "../domain/types";
import { TRACE_SCOPE_LABEL, type PlanItem } from "../store/plan";
import { roleShortText } from "../ui/components/RoleName";
import { pdfHeading, pdfKpis, pdfLines, pdfSave, pdfStart, pdfTable } from "./pdf";

export const TRACE_LABEL: Record<TraceState, string> = { "nie-genutzt": "nie genutzt", genutzt: "genutzt", "kein-trace": "kein Trace" };
const cls = (c: LicenseClass) => CLASS_LABEL[c];

/** Max. Zeilen je Tabelle im PDF; darüber steht ein Verweis auf den CSV-Export. */
export const PDF_ROW_CAP = 300;

/** Positionen in Plan-Reihenfolge, nur die mit Ergebnis. */
export function orderedItems(items: PlanItem[], r: PlanResult): { item: PlanItem; res: PlanItemResult }[] {
  const out: { item: PlanItem; res: PlanItemResult }[] = [];
  for (const item of items) { const res = r.perItem[item.leverId]; if (res && !out.some((o) => o.item.leverId === item.leverId)) out.push({ item, res }); }
  return out;
}

function personRows(rows: PlanPersonRow[]): (string | number)[][] {
  return rows.map((p) => [p.id, p.name ?? "", p.funktion ?? "", p.department ?? p.unit, `${cls(p.from)} -> ${cls(p.to)}`, TRACE_LABEL[p.trace]]);
}
function capped(rows: (string | number)[][]): (string | number)[][] {
  if (rows.length <= PDF_ROW_CAP) return rows;
  return [...rows.slice(0, PDF_ROW_CAP), [`… und ${formatNum(rows.length - PDF_ROW_CAP)} weitere (vollständige Liste im CSV-Export)`, "", "", "", "", ""]];
}

export function planPdf(ds: Dataset, items: PlanItem[], r: PlanResult): void {
  const date = new Date().toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  const p = pdfStart("Umsetzungsplan", `Stand ${date} · ${formatNum(r.items)} Positionen · Zielzustand nach R1–R4`);
  const puffer = r.restLuecke <= 0;
  pdfKpis(p, [
    ["Positionen", formatNum(r.items)],
    ["Personen, die sich abstufen", formatNum(r.persons)],
    ["Reduktion im Zielzustand", `−${formatFue1(r.reduction)}`],
    ["Einsparung je Jahr", `−${formatEuro(r.euroSaved)}`],
    ["Bedarf heute", formatFue(r.ist)],
    ["Bedarf nach Plan", formatFue(r.afterPlan)],
    ["Vertrag", formatFue(r.contracted)],
    [puffer ? "Puffer nach Plan" : "Lücke nach Plan", formatFue(Math.abs(r.restLuecke))],
  ]);
  if (r.missingCombos.length) {
    pdfHeading(p, "Fehlende Verbund-Maßnahmen");
    pdfTable(p, ["Rolle", "Kurztext", "System", "Personen bleiben oben"], r.missingCombos.map((m) => [m.roleId, roleShortText(m.roleId, m.roleName), m.sid, formatNum(m.persons)]));
  }
  const leverById = new Map(ds.levers.map((l) => [l.id, l]));
  orderedItems(items, r).forEach(({ item, res }, i) => {
    const lv = leverById.get(item.leverId); if (!lv) return;
    const short = roleShortText(lv.roleId, lv.roleName);
    pdfHeading(p, `${i + 1}. ${lv.roleId}${short ? ` — ${short}` : ""}`);
    pdfLines(p, [
      `System ${lv.sid} · Landschaft ${landscapeById(lv.landscapeId)?.name ?? lv.landscapeId} · ${lv.authObject} ${lv.field} = ${lv.value}`,
      `Handlung: ${lv.action}`,
      `Umfang: ${TRACE_SCOPE_LABEL[item.scope]} · Aufwand ${lv.effort} · Ticket: ${item.ticket ? "erstellt" : "offen"}`,
    ]);
    pdfKpis(p, [
      ["Träger insgesamt / im Umfang", `${formatNum(res.carriers)} / ${formatNum(res.inScope)}`],
      ["fallen / bleiben oben", `${formatNum(res.fallen.length)} / ${formatNum(res.held)}`],
      ["Anteil an der Reduktion", `−${formatFue1(res.deltaFue)}`],
      ["allein (nur diese Position)", `−${formatFue1(res.alone)}`],
      ["Übergänge", res.transitions.length ? res.transitions.map((t) => `${formatNum(t.persons)} × ${cls(t.fromClass)} -> ${cls(t.toClass)}`).join(" · ") : "—"],
    ]);
    const head = ["Person", "Name", "Funktion (SU01)", "Abteilung", "vorher -> nachher", "Trace"];
    if (res.isSplit) {
      pdfLines(p, [`Erhalten die kleinere Rolle (ohne Wert): ${formatNum(res.kleineRolle.length)} Personen`]);
      if (res.kleineRolle.length) pdfTable(p, head, capped(personRows(res.kleineRolle)));
      pdfLines(p, [`Behalten die größere Rolle (mit Wert): ${formatNum(res.grosseRolle.length)} Personen`]);
      if (res.grosseRolle.length) pdfTable(p, head, capped(personRows(res.grosseRolle)));
    } else {
      if (res.fallen.length) { pdfLines(p, [`Fallen: ${formatNum(res.fallen.length)} Personen`]); pdfTable(p, head, capped(personRows(res.fallen))); }
      if (res.bleibenOben.length) {
        pdfLines(p, [`Bleiben oben: ${formatNum(res.bleibenOben.length)} Personen`]);
        pdfTable(p, ["Person", "Name", "Funktion (SU01)", "Abteilung", "Klasse", "gehalten von"], capped(res.bleibenOben.map((h) => [h.id, h.name ?? "", h.funktion ?? "", h.department ?? h.unit, cls(h.cls), `${h.holderSid} ${h.holderRoleId}${h.holderMissing ? " (Maßnahme fehlt im Plan)" : ""}`])));
      }
    }
  });
  pdfSave(p, `Umsetzungsplan_${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ------------------------------------------------------------ CSV
const esc = (v: string | number) => { const s = String(v ?? ""); return /[;"\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };

export function planCsv(ds: Dataset, items: PlanItem[], r: PlanResult): string {
  const leverById = new Map(ds.levers.map((l) => [l.id, l]));
  const head = ["Nr", "Maßnahme", "Rolle", "Kurztext", "System", "Landschaft", "Handlung", "Umfang", "Ticket", "Person", "Name", "Funktion (SU01)", "Abteilung", "Einheit", "Klasse vorher", "Klasse nachher", "Delta FUE", "Trace", "Status", "Halter System", "Halter Rolle"];
  const lines = [head.join(";")];
  orderedItems(items, r).forEach(({ item, res }, i) => {
    const lv = leverById.get(item.leverId); if (!lv) return;
    const fix = [i + 1, lv.id, lv.roleId, roleShortText(lv.roleId, lv.roleName), lv.sid, landscapeById(lv.landscapeId)?.name ?? lv.landscapeId, lv.action, TRACE_SCOPE_LABEL[item.scope], item.ticket ? "erstellt" : "offen"];
    const push = (cells: (string | number)[]) => lines.push([...fix, ...cells].map(esc).join(";"));
    const heldById = new Map(res.bleibenOben.map((h) => [h.id, h]));
    if (res.isSplit) {
      for (const x of res.kleineRolle) { const h = heldById.get(x.id); push([x.id, x.name ?? "", x.funktion ?? "", x.department ?? "", x.unit, cls(x.from), cls(x.to), x.delta.toFixed(4).replace(".", ","), TRACE_LABEL[x.trace], x.delta > 0 ? "kleine Rolle (ohne Wert) · fällt" : "kleine Rolle (ohne Wert) · bleibt oben", h?.holderSid ?? "", h?.holderRoleId ?? ""]); }
      for (const x of res.grosseRolle) push([x.id, x.name ?? "", x.funktion ?? "", x.department ?? "", x.unit, cls(x.from), cls(x.to), "0", TRACE_LABEL[x.trace], "große Rolle (mit Wert) · außerhalb des Umfangs", "", ""]);
    } else {
      for (const x of res.fallen) push([x.id, x.name ?? "", x.funktion ?? "", x.department ?? "", x.unit, cls(x.from), cls(x.to), x.delta.toFixed(4).replace(".", ","), TRACE_LABEL[x.trace], "fällt", "", ""]);
      for (const h of res.bleibenOben) push([h.id, h.name ?? "", h.funktion ?? "", h.department ?? "", h.unit, cls(h.cls), cls(h.cls), "0", TRACE_LABEL[h.trace], h.holderMissing ? "bleibt oben · Halter-Maßnahme fehlt im Plan" : "bleibt oben", h.holderSid, h.holderRoleId]);
    }
  });
  return "﻿" + lines.join("\r\n") + "\r\n";
}

/** Text als Datei herunterladen (Browser). */
export function downloadText(filename: string, text: string, mime = "text/csv;charset=utf-8"): void {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
